import { useState } from 'react';
import {
  Box,
  Button,
  Center,
  CircularProgress,
  Flex,
  Grid,
  HStack,
  IconButton,
  Image,
  Spinner,
  Text,
  VisuallyHidden,
} from '@chakra-ui/react';
import {
  AlertCircle,
  Clock,
  FileText,
  Mic,
  RotateCcw,
  Trash2,
  X,
} from 'lucide-react';
import type { OutboxEntry, PendingUpload } from '../../store';
import { cancelAttachment, retryAttachment } from '../../lib/attachments';
import { discardMessage, retryMessage } from '../../lib/outbox';
import { ProgressBar } from '../ProgressBar';
import { formatBytes, gatheringQuote } from './messageUtils';
import { useT } from '../../i18n';

const MEDIA_MAX = 320;

const isVisual = (upload: PendingUpload) =>
  (upload.kind === 'image' ||
    upload.kind === 'gif' ||
    upload.kind === 'video') &&
  Boolean(upload.previewUrl);

// "Not sent · Tap to retry", which opens Retry and Delete (D33).
function NotSent({
  error,
  onRetry,
  onDelete,
  deleteLabel,
}: {
  error?: string;
  onRetry: () => void;
  onDelete: () => void;
  deleteLabel?: string;
}) {
  const [open, setOpen] = useState(false);
  const t = useT();
  return (
    <Box pt={1}>
      <HStack
        as="button"
        type="button"
        spacing={1}
        fontSize="13px"
        fontWeight={700}
        color="danger"
        aria-expanded={open}
        onClick={() => setOpen((value) => !value)}
      >
        <AlertCircle size={14} aria-hidden="true" />
        <Text as="span">
          {error
            ? t('chat.pending.notSentError', { error })
            : t('chat.pending.notSentTap')}
        </Text>
      </HStack>
      {open && (
        <HStack spacing={2} pt={2}>
          <Button
            size="sm"
            leftIcon={<RotateCcw size={14} />}
            onClick={onRetry}
          >
            {t('common.retry')}
          </Button>
          <Button
            size="sm"
            variant="outline"
            colorScheme="gray"
            borderColor="line"
            bg="surface"
            color="danger"
            leftIcon={<Trash2 size={14} />}
            onClick={onDelete}
          >
            {deleteLabel ?? t('common.delete')}
          </Button>
        </HStack>
      )}
    </Box>
  );
}

function PendingText({ entry }: { entry: OutboxEntry }) {
  const t = useT();
  return (
    <Box>
      {entry.replyTo && (
        <Box
          bg="surface"
          borderLeftWidth="3px"
          borderLeftColor="accent"
          borderRadius="12px"
          px={3}
          py={2}
          my={1}
        >
          <Text fontSize="13px" fontWeight={700} color="accent" noOfLines={1}>
            {entry.replyTo.senderName}
          </Text>
          <Text fontSize="14px" color="ink.body" noOfLines={2}>
            {entry.replyTo.preview}
          </Text>
        </Box>
      )}
      <Text
        fontSize="17px"
        lineHeight="25px"
        whiteSpace="pre-wrap"
        overflowWrap="anywhere"
        opacity={entry.status === 'failed' ? 0.7 : 1}
      >
        {/* A poll on its way shows its question (M60, D220), a gathering
            its title (M71, D282). */}
        {entry.poll
          ? t('chat.poll.quote', { question: entry.content })
          : entry.gathering
            ? gatheringQuote(entry.content)
            : entry.content}
      </Text>
      {entry.status === 'failed' && (
        <NotSent
          error={
            entry.needsUpdate
              ? t('common.updateRequired.needsUpdate')
              : entry.rejected
                ? entry.error
                : undefined
          }
          onRetry={() => retryMessage(entry.clientId)}
          onDelete={() => discardMessage(entry.clientId)}
        />
      )}
    </Box>
  );
}

// Several photos at once: small tiles and one bar, "2 of 3 photos".
function PhotoBatch({ uploads }: { uploads: PendingUpload[] }) {
  const done = uploads.filter((upload) => upload.status === 'sending').length;
  const progress =
    uploads.reduce((sum, upload) => sum + upload.progress, 0) / uploads.length;
  const failed = uploads.filter((upload) => upload.status === 'failed');
  const allVideo = uploads.every((upload) => upload.kind === 'video');
  const t = useT();
  return (
    <Box pt={1}>
      <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap="6px" maxW="360px">
        {uploads.map((upload) => (
          <Box
            key={upload.id}
            position="relative"
            h="72px"
            borderRadius="12px"
            overflow="hidden"
            bg="gray.300"
          >
            <Image
              src={upload.previewUrl ?? undefined}
              alt={upload.fileName}
              w="full"
              h="full"
              objectFit="cover"
              opacity={upload.status === 'uploading' ? 0.6 : 1}
            />
            {upload.status === 'uploading' && (
              <Center position="absolute" inset={0}>
                <Spinner
                  size="sm"
                  color="accent"
                  thickness="3px"
                  emptyColor="rgba(122, 31, 43, 0.25)"
                />
              </Center>
            )}
          </Box>
        ))}
      </Grid>
      {failed.length === 0 ? (
        <HStack spacing="10px" pt={2} maxW="360px">
          <Box flex="1" sx={{ '[role=progressbar]': { bg: 'surface' } }}>
            <ProgressBar
              done={Math.round(progress * 100)}
              total={100}
              label={t(
                allVideo
                  ? 'chat.pending.sendingVideos'
                  : 'chat.pending.sendingPhotos'
              )}
            />
          </Box>
          <Text
            fontSize="13px"
            fontWeight={600}
            color="ink.body"
            flexShrink={0}
          >
            {t(
              allVideo ? 'chat.pending.videosDone' : 'chat.pending.photosDone',
              {
                n: done,
                total: uploads.length,
              }
            )}
          </Text>
        </HStack>
      ) : (
        <NotSent
          error={failed.length === 1 ? failed[0]?.error : undefined}
          onRetry={() => failed.forEach((upload) => retryAttachment(upload.id))}
          onDelete={() =>
            failed.forEach((upload) => cancelAttachment(upload.id))
          }
          deleteLabel={t('common.remove')}
        />
      )}
    </Box>
  );
}

// An album on its way (M60, D219): its tiles, each filling as its file goes
// up, one bar for the whole album, and one Retry for what didn't make it.
function PendingAlbum({ upload }: { upload: PendingUpload }) {
  const t = useT();
  const items = upload.items ?? [];
  const done = items.filter((item) => item.done).length;
  const failed = upload.status === 'failed';
  const allVideo = items.every((item) => item.kind === 'video');
  return (
    <Box pt={1} data-testid="pending-album">
      <Grid templateColumns="repeat(3, minmax(0, 1fr))" gap="6px" maxW="360px">
        {items.map((item, index) => (
          <Box
            key={index}
            position="relative"
            h="72px"
            borderRadius="12px"
            overflow="hidden"
            bg="gray.300"
          >
            {item.previewUrl && (
              <Image
                src={item.previewUrl}
                alt=""
                w="full"
                h="full"
                objectFit="cover"
                opacity={item.done ? 1 : 0.6}
              />
            )}
            {!item.done && !failed && (
              <Center position="absolute" inset={0}>
                <CircularProgress
                  value={Math.round(item.progress * 100)}
                  size="32px"
                  thickness="10px"
                  color="accent"
                  trackColor="rgba(255, 255, 255, 0.6)"
                />
              </Center>
            )}
          </Box>
        ))}
      </Grid>
      {!failed && (
        <HStack spacing="10px" pt={2} maxW="360px">
          <Box flex="1" sx={{ '[role=progressbar]': { bg: 'surface' } }}>
            <ProgressBar
              done={Math.round(upload.progress * 100)}
              total={100}
              label={t(
                allVideo
                  ? 'chat.pending.sendingVideos'
                  : 'chat.pending.sendingPhotos'
              )}
            />
          </Box>
          <Text
            fontSize="13px"
            fontWeight={600}
            color="ink.body"
            flexShrink={0}
          >
            {t(
              allVideo ? 'chat.pending.videosDone' : 'chat.pending.photosDone',
              { n: done, total: items.length }
            )}
          </Text>
        </HStack>
      )}
      {upload.retrying && <RetryingNote />}
      {failed && (
        <NotSent
          error={upload.error}
          onRetry={() => retryAttachment(upload.id)}
          onDelete={() => cancelAttachment(upload.id)}
          deleteLabel={t('common.remove')}
        />
      )}
    </Box>
  );
}

function PendingAttachment({ upload }: { upload: PendingUpload }) {
  const failed = upload.status === 'failed';
  const percent = Math.round(upload.progress * 100);
  const t = useT();

  if (isVisual(upload)) {
    const scale =
      upload.width && upload.height
        ? Math.min(1, MEDIA_MAX / upload.width, MEDIA_MAX / upload.height)
        : 1;
    return (
      <Box pt={1}>
        <Box position="relative" display="inline-block" maxW="100%">
          <Image
            src={upload.previewUrl ?? undefined}
            alt={upload.fileName}
            w={
              upload.width ? `${Math.round(upload.width * scale)}px` : undefined
            }
            maxW={`min(${MEDIA_MAX}px, 100%)`}
            h="auto"
            sx={
              upload.width && upload.height
                ? { aspectRatio: `${upload.width} / ${upload.height}` }
                : undefined
            }
            objectFit="cover"
            borderRadius="20px"
            opacity={failed ? 0.5 : 0.8}
          />
          {!failed && (
            <Center position="absolute" inset={0}>
              <Center
                w="52px"
                h="52px"
                borderRadius="full"
                bg="rgba(31, 29, 27, 0.55)"
              >
                <Spinner
                  color="white"
                  emptyColor="whiteAlpha.400"
                  thickness="4px"
                  size="lg"
                  aria-label={
                    upload.status === 'sending'
                      ? t('chat.pending.sending')
                      : t('chat.pending.uploading', { percent })
                  }
                />
              </Center>
            </Center>
          )}
        </Box>
        {upload.retrying && <RetryingNote />}
        {failed && (
          <NotSent
            error={upload.error}
            onRetry={() => retryAttachment(upload.id)}
            onDelete={() => cancelAttachment(upload.id)}
            deleteLabel={t('common.remove')}
          />
        )}
      </Box>
    );
  }

  // Files and voice notes: the file card, filling as it uploads.
  return (
    <Box pt={1}>
      <HStack
        spacing={3}
        p="10px"
        borderRadius="18px"
        bg="surface"
        borderWidth="1px"
        borderColor="line"
        maxW="360px"
        opacity={failed ? 0.7 : 1}
      >
        <Flex
          w="44px"
          h="44px"
          borderRadius="12px"
          bg="surface.subtle"
          color="ink.body"
          align="center"
          justify="center"
          flexShrink={0}
          aria-hidden="true"
        >
          {upload.kind === 'voice' ? <Mic size={20} /> : <FileText size={20} />}
        </Flex>
        <Box flex="1" minW={0}>
          <Text fontSize="15px" fontWeight={700} noOfLines={1}>
            {upload.kind === 'voice' ? t('chat.type.voice') : upload.fileName}
          </Text>
          {!failed && (
            <Box pt="6px">
              <ProgressBar
                done={percent}
                total={100}
                label={t('chat.pending.sendingFile', { name: upload.fileName })}
              />
            </Box>
          )}
          <Text fontSize="13px" color="muted" pt={failed ? 0 : '6px'}>
            {failed
              ? formatBytes(upload.fileSize)
              : upload.status === 'sending'
                ? t('chat.pending.sendingDots')
                : t('chat.file.progress', {
                    done: formatBytes(upload.fileSize * upload.progress),
                    total: formatBytes(upload.fileSize),
                  })}
          </Text>
        </Box>
        {!failed && (
          <IconButton
            aria-label={t('chat.pending.cancel')}
            icon={<X size={20} />}
            colorScheme="gray"
            bg="surface.subtle"
            flexShrink={0}
            onClick={() => cancelAttachment(upload.id)}
          />
        )}
      </HStack>
      {upload.retrying && <RetryingNote />}
      {failed && (
        <NotSent
          error={upload.error}
          onRetry={() => retryAttachment(upload.id)}
          onDelete={() => cancelAttachment(upload.id)}
          deleteLabel={t('common.remove')}
        />
      )}
    </Box>
  );
}

// Everything of yours still on its way, at the end of the timeline as one of
// your own rows (Living Room "Sending…"): text from the outbox (M23) and
// attachments uploading (issue #2).
export function PendingBand({
  entries,
  uploads,
}: {
  entries: OutboxEntry[];
  uploads: PendingUpload[];
}) {
  const t = useT();
  if (entries.length === 0 && uploads.length === 0) return null;
  const anyFailed =
    entries.some((entry) => entry.status === 'failed') ||
    uploads.some((upload) => upload.status === 'failed');
  const visuals = uploads.filter(isVisual);
  const others = uploads.filter((upload) => !isVisual(upload));

  // One of your own bubbles, on the right (M68, D259): no "You" disc or name,
  // just what's on its way and how it's going.
  return (
    <Flex
      justify="flex-end"
      mt="12px"
      data-testid="pending-band"
      data-side="mine"
    >
      <Box
        maxW="80%"
        minW="48px"
        px="12px"
        py="8px"
        bg="accent.tint"
        borderRadius="20px"
        role="status"
        aria-live="polite"
      >
        <VisuallyHidden as="span">{t('common.you')}: </VisuallyHidden>
        <HStack spacing={2} align="baseline" mb="2px" justify="flex-end">
          <HStack
            as="span"
            spacing={1}
            fontSize="13px"
            color={anyFailed ? 'danger' : 'muted'}
          >
            {anyFailed ? (
              <AlertCircle size={13} aria-hidden="true" />
            ) : (
              <Clock size={13} aria-hidden="true" />
            )}
            <Text as="span">
              {anyFailed
                ? t('chat.pending.notSent')
                : t('chat.pending.sendingDots')}
            </Text>
          </HStack>
        </HStack>
        {entries.map((entry) => (
          <PendingText key={entry.clientId} entry={entry} />
        ))}
        {visuals.length > 1 ? (
          <PhotoBatch uploads={visuals} />
        ) : (
          visuals.map((upload) => (
            <PendingAttachment key={upload.id} upload={upload} />
          ))
        )}
        {others.map((upload) =>
          upload.kind === 'album' ? (
            <PendingAlbum key={upload.id} upload={upload} />
          ) : (
            <PendingAttachment key={upload.id} upload={upload} />
          )
        )}
      </Box>
    </Flex>
  );
}

// Shown while an upload waits to try again by itself (D134).
function RetryingNote() {
  const t = useT();
  return (
    <Text fontSize="13px" color="muted" pt={1} role="status">
      {t('chat.pending.retrying')}
    </Text>
  );
}
